import React, { useEffect, useMemo, useState } from 'react';
import { CsvImporterConfig, CsvFieldTransformConfig, CsvEncoding } from '@/lib/csv-import/csv-import.types';
import { useCsvImportExecutor, useCsvImportState, useCsvValidation } from '@/lib/csv-import/csv-import.hooks';
import { autoMapCsvColumns } from '@/lib/csv-import/csv-import.utils';
import { Button } from '@/ui-components/ui/button';
import { StandardDialogHeader } from '@/util-components/dialog/components/StandardDialogHeader';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/ui-components/ui/dialog';
import { PermissionFeatureWrapper } from '@/util-components/auth/PermissionFeatureWrapper';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';

interface CsvImportModalProps<TDto = any, TMeta = any> {
  isOpen: boolean;
  onClose: () => void;
  config: CsvImporterConfig<TDto, TMeta>;
}

export function CsvImportModal<TDto = any, TMeta = any>({
  isOpen,
  onClose,
  config,
}: CsvImportModalProps<TDto, TMeta>) {
  const state = useCsvImportState<TDto>();
  const { context, isValidating, hasErrors, previewRows, revalidate } = useCsvValidation<TDto, TMeta>(config, state);
  const { isImporting, startImport } = useCsvImportExecutor<TDto, TMeta>(config, state);
  const [needsValidation, setNeedsValidation] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  // NEU: allgemeiner manueller Edit-State pro Zeile/Feld
  const [manualEdits, setManualEdits] = useState<
    Record<number, Record<string, any>>
  >({});

  useEffect(() => {
    state.setManualOverrides(() => {
      // manualEdits ist bereits im passenden Format: rowIndex -> fieldKey -> value
      return manualEdits;
    });
  }, [manualEdits, state]);

  // Für ein Feld herausfinden, auf welche CSV-Spalte es aktuell gemappt ist
  const getColumnIndexForField = React.useCallback((fieldKey: string): number | null => {
    for (const [indexStr, mappedField] of Object.entries(state.mapping)) {
      if (mappedField && String(mappedField.key) === fieldKey) {
        return Number(indexStr);
      }
    }
    return null;
  }, [state.mapping]);

  // Initiales Auto-Mapping, sobald Header da sind und noch kein Mapping gesetzt ist
  useEffect(() => {
    if (!state.headers.length) return;
    if (Object.keys(state.mapping).length > 0) return;

    const initialMapping = autoMapCsvColumns(state.headers, config);
    state.setMapping(() => initialMapping as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.headers, config]);

  // Teams (oder andere Relationen) aus dem Kontext holen
  const relationOptions = context?.relationOptions || {};

  // Prüfen, ob es überhaupt requiredRelation-Felder gibt
  const requiredRelationFields = useMemo(
    () => config.fields.filter((f) => f.type === 'relation' && f.requiredRelation),
    [config.fields],
  );

  // Helper: hat eine Zeile für alle requiredRelation-Felder einen Wert (CSV oder manuell)?
  const hasAllRequiredRelations = useMemo(() => {
    if (!requiredRelationFields.length) return true;

    return previewRows.every((row) => {
      const rowIndex = row.originalRowIndex;
      return requiredRelationFields.every((field) => {
        const fieldKey = String(field.key);
        const colIndex = getColumnIndexForField(fieldKey);

        if (colIndex !== null) {
          // CSV-Feld gemappt: Wert muss im mappedValues vorhanden sein
          const mapped = (row.mappedValues as any)[fieldKey];
          return !!mapped;
        }

        // Keine Spalte gemappt: manuelle Auswahl notwendig
        const manual = manualEdits[rowIndex]?.[fieldKey];
        return !!manual;
      });
    });
  }, [previewRows, requiredRelationFields, manualEdits, getColumnIndexForField]);

  const canImport = useMemo(
    () =>
      !isValidating &&
      !isImporting &&
      !hasErrors &&
      state.status.validRows > 0 &&
      hasAllRequiredRelations,
    [hasErrors, isImporting, isValidating, state.status.validRows, hasAllRequiredRelations],
  );

  // Live-Validierung bei Datei / Mapping-Änderung
  useEffect(() => {
    if (!state.rows.length) return;
    setNeedsValidation(true);
  }, [state.rows, state.mapping]);

  useEffect(() => {
    if (!needsValidation) return;
    setNeedsValidation(false);
    revalidate();
  }, [needsValidation, revalidate]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    state.setFile(file ?? null);
  };

  // Neue Handler für Drag&Drop
  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
      state.setFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  // Hilfsfunktion: Standard-Transforms für ein Feld basierend auf einer Auswahl setzen
  const buildSplitTransform = (part: 'first' | 'second'): CsvFieldTransformConfig[] => {
    const index = part === 'first' ? 0 : 1;
    return [
      {
        type: 'splitPart',
        config: {
          delimiter: ' ',
          index,
          trimPart: true,
        },
      },
    ];
  };

  // Prüfen, ob in der gewählten CSV-Spalte irgendwo ein (normales) Leerzeichen vorkommt
  const columnHasSpace = (columnIndex: number | null): boolean => {
    if (columnIndex === null) return false;
    return state.rows.some((row) => {
      const cell = row[columnIndex] ?? '';
      const str = String(cell);
      // Nur klassisches Leerzeichen, wie gewünscht
      return str.trim().includes(' ');
    });
  };

  // Feld -> CSV-Spalte mappen (ein Dropdown pro Feld)
  const handleFieldMappingChange = (fieldKey: string, columnIndex: number | null) => {
    setManualEdits((prev) => {
      const next: typeof prev = {};
      Object.entries(prev).forEach(([rowIndexStr, rowEdits]) => {
        const rowIndex = Number(rowIndexStr);
        const { [fieldKey]: _removed, ...rest } = rowEdits;
        next[rowIndex] = rest;
      });
      return next;
    });
    state.setManualOverrides((prev) => {
      const next: typeof prev = {};
      Object.entries(prev).forEach(([rowIndexStr, rowEdits]) => {
        const rowIndex = Number(rowIndexStr);
        const { [fieldKey]: _removed, ...rest } = rowEdits;
        next[rowIndex] = rest;
      });
      return next;
    });

    state.setMapping((prev) => {
      const cloned = { ...prev };

      // Wenn Feld auf "keine Spalte" gesetzt wird: alle bisherigen Zuordnungen dieses Feldes löschen
      if (columnIndex === null) {
        Object.entries(cloned).forEach(([idxStr, mappedField]) => {
          if (mappedField && String(mappedField.key) === fieldKey) {
            cloned[Number(idxStr)] = null;
          }
        });
        return cloned;
      }

      // Feld in der ausgewählten CSV-Spalte setzen, ohne andere Felder zu beeinflussen
      const baseFieldConfig = config.fields.find((f) => String(f.key) === fieldKey) ?? null;
      if (!baseFieldConfig) {
        cloned[columnIndex] = null;
        return cloned;
      }

      // Beim Setzen der Spalte keine Transformationsauswahl treffen -> bestehende Transforms (falls gesetzt) übernehmen
      const existingAtColumn = cloned[columnIndex];
      const transforms = existingAtColumn?.transforms ?? baseFieldConfig.transforms;

      cloned[columnIndex] = {
        ...baseFieldConfig,
        transforms,
      } as any;
      return cloned;
    });
  };

  // Transformation für ein Feld ändern (keine / erster Teil / zweiter Teil)
  const handleFieldTransformChange = (fieldKey: string, mode: 'none' | 'first' | 'second') => {
    state.setMapping((prev) => {
      const cloned = { ...prev };

      Object.entries(cloned).forEach(([idxStr, mappedField]) => {
        if (!mappedField || String(mappedField.key) !== fieldKey) return;

        if (mode === 'none') {
          // Transforms entfernen
          cloned[Number(idxStr)] = {
            ...mappedField,
            transforms: undefined,
          } as any;
        } else {
          cloned[Number(idxStr)] = {
            ...mappedField,
            transforms: buildSplitTransform(mode),
          } as any;
        }
      });

      return cloned;
    });
  };

  // Für ein Feld die aktuell konfigurierte Transformations-Auswahl ermitteln
  const getFieldTransformMode = (fieldKey: string): 'none' | 'first' | 'second' => {
    for (const [, mappedField] of Object.entries(state.mapping)) {
      if (!mappedField || String(mappedField.key) !== fieldKey) continue;
      const transforms = mappedField.transforms;
      if (!transforms || transforms.length === 0) return 'none';
      const step = transforms[0];
      if (step.type === 'splitPart') {
        const idx = step.config?.index ?? 0;
        if (idx === 0) return 'first';
        if (idx === 1) return 'second';
      }
    }
    return 'none';
  };

  const handleImportClick = async () => {
    let rowsToImport = previewRows;

    // NEU: manuelle Edits (alle Feldtypen) und manuelle Relationswerte in mappedValues mergen
    if (Object.keys(manualEdits).length) {
      rowsToImport = previewRows.map((row) => {
        const rowIndex = row.originalRowIndex;
        const editsForRow = manualEdits[rowIndex] || {};
        const mergedMappedValues = { ...(row.mappedValues as any) };

        // Alle sonstigen manuellen Edits (Text, Zahl, Datum, Boolean etc.) übernehmen
        Object.entries(editsForRow).forEach(([fieldKey, value]) => {
          if (value !== undefined) {
            mergedMappedValues[fieldKey] = value;
          }
        });

        return {
          ...row,
          mappedValues: mergedMappedValues,
        };
      }) as any;

      state.setParsedRows(rowsToImport as any);
    }

    await startImport(rowsToImport as any);
    // Nach abgeschlossenem Import den State zurücksetzen, damit der nächste Öffnung leer ist
    state.reset();
    onClose();
  };

  // Vorschau: standardmäßig alle Zeilen anzeigen; falls config.previewRowCount gesetzt ist, diesen Wert nutzen
  const effectivePreviewCount = previewRows.length;


  const handleClose = () => {
    state.reset();
    onClose();
  };

  // Fehler pro Zeile/Feld für die Anzeige in der Tabelle aufbereiten
  const errorsByRowAndField = useMemo(() => {
    const map: Record<number, Record<string, string[]>> = {};
    previewRows.forEach((row) => {
      const rowIndex = row.originalRowIndex;
      const fieldMap: Record<string, string[]> = {};
      row.errors.forEach((err) => {
        if (!err.fieldKey) return;
        const key = String(err.fieldKey);
        if (!fieldMap[key]) fieldMap[key] = [];
        fieldMap[key].push(err.message);
      });
      map[rowIndex] = fieldMap;
    });
    return map;
  }, [previewRows]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-[96vw] max-w-[1400px] p-0 border-0 shadow-2xl max-h-[85vh] flex flex-col overflow-hidden gap-0">
        <div className="px-6 py-5 shrink-0">
          <StandardDialogHeader
            icon={FileSpreadsheet}
            title={`${config.entityName} per CSV importieren`}
            description="Laden Sie eine CSV-Datei hoch und ordnen Sie die Spalten zu."
            className="p-0 border-none"
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden px-6 pb-6 pt-0 gap-6">
            {state.headers.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                    <label
                    className={`flex flex-col items-center justify-center w-full max-w-xl px-8 py-12 border-2 border-dashed rounded-2xl text-center cursor-pointer shadow-sm transition-colors ${
                        isDragActive
                        ? 'border-primary bg-primary/10'
                        : 'border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    >
                    {/* Icon-Kreis */}
                    <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary">
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="w-6 h-6"
                        >
                        <path d="M12 16V4" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8 8L12 4L16 8" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M5 16H4C3.46957 16 2.96086 16.2107 2.58579 16.5858C2.21071 16.9609 2 17.4696 2 18C2 18.5304 2.21071 19.0391 2.58579 19.4142C2.96086 19.7893 3.46957 20 4 20H20C20.5304 20 21.0391 19.7893 21.4142 19.4142C21.7893 19.0391 22 18.5304 22 18C22 17.4696 21.7893 16.9609 21.4142 16.5858C21.0391 16.2107 20.5304 16 20 16H19" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>

                    <span className="text-base font-semibold mb-1 text-foreground">
                        CSV-Datei hier ablegen oder klicken, um eine Datei auszuwählen
                    </span>
                    <span className="text-xs text-muted-foreground mb-4 max-w-md">
                        Unterstützt: .csv mit Kopfzeile. Die erste Zeile sollte die Spaltennamen enthalten, damit du sie bequem zuordnen kannst.
                    </span>
                    <span className="inline-flex items-center justify-center px-4 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground shadow-sm">
                        Datei auswählen
                    </span>
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    </label>
                </PermissionFeatureWrapper>
              </div>
            ) : (
              <>
                {/* Kein separater CSV-Datei-Label-Block mehr im Nicht-Empty-State */}
                <div className="flex-1 flex flex-col overflow-hidden gap-3 min-h-0">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm font-medium">
                      Felder zuordnen &amp; Vorschau
                      <span className="ml-2 text-xs text-muted-foreground">
                        (Spalten sind Zielfelder, erste Zeile Mapping, darunter gemappte Daten)
                      </span>
                    </p>

                    {/* NEU: Encoding-Info + manuelle Auswahl */}
                    {state.meta?.encoding && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-[11px]">Zeichensatz:</span>
                          <select
                            className="border rounded px-1 py-0.5 text-[11px] bg-background"
                            value={state.meta.encoding.selected}
                            onChange={(e) => state.setEncoding(e.target.value as CsvEncoding)}
                          >
                            <option value="utf-8">UTF-8</option>
                            <option value="windows-1252">Windows-1252</option>
                            <option value="iso-8859-1">ISO-8859-1</option>
                            <option value="latin1">Latin-1</option>
                          </select>
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {state.status.totalRows} Zeilen, {state.status.validRows} gültig, {state.status.errorRows} mit Fehlern
                  </div>

                  {/* Tabelle nimmt den restlichen Platz ein */}
                  <div className="flex-1 min-h-0 border rounded-md overflow-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-muted">
                          {config.fields.map((field) => (
                            <th key={String(field.key)} className="px-2 py-1 border-r text-left">
                              {field.label}
                              {field.required ? ' *' : ''}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Mapping-Zeile: pro Feld ein Dropdown zur Auswahl einer CSV-Spalte + Transform-Auswahl */}
                        <tr className="bg-muted/40">
                          {config.fields.map((field) => {
                            const fieldKey = String(field.key);
                            const mappedColIndex = getColumnIndexForField(fieldKey);
                            const transformMode = getFieldTransformMode(fieldKey);
                            const canShowSplitOptions = columnHasSpace(mappedColIndex);

                            return (
                              <td key={fieldKey} className="px-2 py-1 border-t border-r align-top">
                                <div className="space-y-1">
                                  <select
                                    className="border rounded px-1 py-0.5 text-[11px] w-full"
                                    value={mappedColIndex !== null ? String(mappedColIndex) : ''}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      handleFieldMappingChange(fieldKey, value ? Number(value) : null);
                                    }}
                                  >
                                    <option value="">Keine Spalte zugeordnet</option>
                                    {state.headers.map((header, idx) => (
                                      <option key={idx} value={idx}>
                                        {header || `Spalte ${idx + 1}`}
                                      </option>
                                    ))}
                                  </select>

                                  {/* Transform-Auswahl nur, wenn in der Spalte ein Leerzeichen vorkommt */}
                                  {mappedColIndex !== null && canShowSplitOptions && (
                                    <select
                                      className="border rounded px-1 py-0.5 text-[10px] w-full bg-background"
                                      value={transformMode}
                                      onChange={(e) =>
                                        handleFieldTransformChange(fieldKey, e.target.value as 'none' | 'first' | 'second')
                                      }
                                    >
                                      <option value="none">Originalwert verwenden</option>
                                      <option value="first">Erster Teil vor erstem Leerzeichen</option>
                                      <option value="second">Zweiter Teil nach erstem Leerzeichen</option>
                                    </select>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>

                        {/* Gemappte Datenzeilen: pro Feld die gemappten Werte anzeigen */}
                        {previewRows.slice(0, effectivePreviewCount).map((row) => {
                          const hasRowErrors = row.errors.length > 0;
                          const rowIndex = row.originalRowIndex;
                          const rowFieldErrors = errorsByRowAndField[rowIndex] || {};

                          // Prüfen, ob für diese Zeile eine requiredRelation (z. B. Mannschaft) fehlt
                          const hasMissingRequiredRelation = requiredRelationFields.some((field) => {
                            const fieldKey = String(field.key);
                            const colIndex = getColumnIndexForField(fieldKey);

                            if (colIndex !== null) {
                              // Es gibt eine CSV-Spalte: Wert im mappedValues muss vorhanden sein
                              const mapped = (row.mappedValues as any)[fieldKey];
                              return !mapped;
                            }

                            // Keine Spalte gemappt: dann muss eine manuelle Auswahl existieren
                            const manual = manualEdits[rowIndex]?.[fieldKey];
                            return !manual;
                          });

                          const rowHasProblem = hasRowErrors || hasMissingRequiredRelation;

                          return (
                            <tr
                              key={row.originalRowIndex}
                              className={rowHasProblem ? 'bg-destructive/10' : ''}
                            >
                              {config.fields.map((field) => {
                                const key = field.key as string;
                                const value = (row.mappedValues as any)[key];
                                const isRequiredRelation = field.type === 'relation' && field.requiredRelation;
                                const colIndex = getColumnIndexForField(String(field.key));
                                const fieldErrors = rowFieldErrors[key] || [];
                                const hasFieldError = fieldErrors.length > 0;

                                // Wenn requiredRelation und keine Spalte gemappt ist: manuelle Auswahl anzeigen
                                if (isRequiredRelation && colIndex === null) {
                                  const options = field.relationKey ? relationOptions[field.relationKey] || [] : [];
                                  const selected = manualEdits[rowIndex]?.[key] ?? '';
                                  const missing = !selected;

                                  return (
                                    <td
                                      key={key}
                                      className={
                                        'px-2 py-1 border-t border-r align-top' +
                                        ((missing || hasFieldError) ? ' bg-destructive/10' : '')
                                      }
                                    >
                                      <select
                                        className={
                                          'border rounded px-1 py-0.5 text-[11px] w-full' +
                                          ((missing || hasFieldError) ? ' border-destructive' : '')
                                        }
                                        value={selected}
                                        onChange={(e) => {
                                          const newVal = e.target.value || undefined;
                                          setManualEdits((prev) => {
                                            const next = {
                                              ...prev,
                                              [rowIndex]: {
                                                ...(prev[rowIndex] || {}),
                                                [key]: newVal,
                                              },
                                            };
                                            state.setManualOverrides(() => next);
                                            return next;
                                          });
                                        }}
                                      >
                                        <option value="">Mannschaft auswählen...</option>
                                        {options.map((opt) => (
                                          <option key={opt.id} value={opt.id}>
                                            {opt.name}
                                          </option>
                                        ))}
                                      </select>
                                      {hasFieldError && (
                                        <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                          {fieldErrors[0]}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                // NEU: generischen Input pro Feld/Zeile rendern, typgerecht
                                const editedValue = manualEdits[rowIndex]?.[key];
                                const displayValue = editedValue ?? value;

                                const commonInputClasses =
                                  'w-full border rounded px-1 py-0.5 text-[11px] bg-background';

                                const baseTdClass =
                                  'px-2 py-1 border-t border-r align-top' +
                                  (hasFieldError ? ' bg-destructive/5' : '');

                                if (field.type === 'boolean') {
                                  return (
                                    <td key={key} className={baseTdClass}>
                                      <select
                                        className={
                                          commonInputClasses + (hasFieldError ? ' border-destructive' : '')
                                        }
                                        value={displayValue === true ? 'true' : displayValue === false ? 'false' : ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          const boolVal = v === 'true' ? true : v === 'false' ? false : undefined;
                                          setManualEdits((prev) => {
                                            const next = {
                                              ...prev,
                                              [rowIndex]: { ...(prev[rowIndex] || {}), [key]: boolVal },
                                            };
                                            state.setManualOverrides(() => next);
                                            return next;
                                          });
                                        }}
                                      >
                                        <option value="">-</option>
                                        <option value="true">Ja</option>
                                        <option value="false">Nein</option>
                                      </select>
                                      {hasFieldError && (
                                        <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                          {fieldErrors[0]}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                if (field.type === 'number') {
                                  return (
                                    <td key={key} className={baseTdClass}>
                                      <input
                                        type="number"
                                        className={
                                          commonInputClasses + (hasFieldError ? ' border-destructive' : '')
                                        }
                                        value={displayValue ?? ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          const numVal = v === '' ? undefined : Number(v);
                                          setManualEdits((prev) => {
                                            const next = {
                                              ...prev,
                                              [rowIndex]: {
                                                ...(prev[rowIndex] || {}),
                                                [key]: Number.isNaN(numVal) ? undefined : numVal,
                                              },
                                            };
                                            state.setManualOverrides(() => next);
                                            return next;
                                          });
                                        }}
                                      />
                                      {hasFieldError && (
                                        <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                          {fieldErrors[0]}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                if (field.type === 'date') {
                                  // ISO-String -> yyyy-MM-dd für Input
                                  const iso = typeof displayValue === 'string' ? displayValue : value;
                                  const dateValue = iso ? new Date(iso) : null;
                                  const inputValue =
                                    dateValue && !Number.isNaN(dateValue.getTime())
                                      ? `${dateValue.getFullYear()}-${String(
                                          dateValue.getMonth() + 1,
                                        ).padStart(2, '0')}-${String(dateValue.getDate()).padStart(2, '0')}`
                                      : '';

                                  return (
                                    <td key={key} className={baseTdClass}>
                                      <input
                                        type="date"
                                        className={
                                          commonInputClasses + (hasFieldError ? ' border-destructive' : '')
                                        }
                                        value={inputValue}
                                        onChange={(e) => {
                                          const v = e.target.value; // yyyy-MM-dd oder ''
                                          let isoVal: string | undefined;
                                          if (v) {
                                            const d = new Date(v + 'T00:00:00');
                                            isoVal = Number.isNaN(d.getTime()) ? undefined : d.toISOString();
                                          }
                                          setManualEdits((prev) => {
                                            const next = {
                                              ...prev,
                                              [rowIndex]: { ...(prev[rowIndex] || {}), [key]: isoVal },
                                            };
                                            state.setManualOverrides(() => next);
                                            return next;
                                          });
                                        }}
                                      />
                                      {hasFieldError && (
                                        <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                          {fieldErrors[0]}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                // Für relation-Felder mit gemappter Spalte: einfachen Textinput für Anzeige/Override
                                if (field.type === 'relation' && colIndex !== null) {
                                  return (
                                    <td key={key} className={baseTdClass}>
                                      <input
                                        type="text"
                                        className={
                                          commonInputClasses + (hasFieldError ? ' border-destructive' : '')
                                        }
                                        value={displayValue ?? ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          setManualEdits((prev) => {
                                            const next = {
                                              ...prev,
                                              [rowIndex]: {
                                                ...(prev[rowIndex] || {}),
                                                [key]: v || undefined,
                                              },
                                            };
                                            state.setManualOverrides(() => next);
                                            return next;
                                          });
                                        }}
                                      />
                                      {hasFieldError && (
                                        <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                          {fieldErrors[0]}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                // Enum-Felder: Dropdown mit erlaubten Werten (kein Freitext)
                                if (field.type === 'enum' && field.enumValues && field.enumValues.length > 0) {
                                  const selected = displayValue ?? '';
                                  return (
                                    <td key={key} className={baseTdClass}>
                                      <select
                                        className={
                                          commonInputClasses + (hasFieldError ? ' border-destructive' : '')
                                        }
                                        value={selected}
                                        onChange={(e) => {
                                          const v = e.target.value || undefined;
                                          setManualEdits((prev) => {
                                            const next = {
                                              ...prev,
                                              [rowIndex]: {
                                                ...(prev[rowIndex] || {}),
                                                [key]: v,
                                              },
                                            };
                                            state.setManualOverrides(() => next);
                                            return next;
                                          });
                                        }}
                                      >
                                        <option value="">– Keine Auswahl –</option>
                                        {field.enumValues.map((enumVal) => (
                                          <option key={enumVal} value={enumVal}>
                                            {field.enumLabels?.[enumVal] ?? enumVal}
                                          </option>
                                        ))}
                                      </select>
                                      {hasFieldError && (
                                        <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                          {fieldErrors[0]}
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                // Standard: Textinput für string und sonstige Typen
                                return (
                                  <td key={key} className={baseTdClass}>
                                    <input
                                      type="text"
                                      className={
                                        commonInputClasses + (hasFieldError ? ' border-destructive' : '')
                                      }
                                      value={displayValue ?? ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setManualEdits((prev) => {
                                          const next = {
                                            ...prev,
                                            [rowIndex]: {
                                              ...(prev[rowIndex] || {}),
                                              [key]: v || undefined,
                                            },
                                          };
                                          state.setManualOverrides(() => next);
                                          return next;
                                        });
                                      }}
                                    />
                                    {hasFieldError && (
                                      <div className="mt-0.5 text-[10px] text-destructive leading-tight">
                                        {fieldErrors[0]}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {state.meta?.warnings?.length ? (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                      {state.meta.warnings[0]}
                    </div>
                  ) : null}

                  {isValidating && <div className="text-xs">Validierung läuft...</div>}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 shrink-0 bg-background border-t flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col justify-center gap-1 text-xs text-muted-foreground w-full sm:w-auto text-center sm:text-left">
            {state.status.importedRows > 0 || state.status.failedRows > 0 ? (
              <span>
                Importiert: {state.status.importedRows}, Fehlgeschlagen: {state.status.failedRows}
              </span>
            ) : null}

            {/* Allgemeiner Hinweis bei Validierungsfehlern (z.B. leere Pflichtfelder wie Vorname) */}
            {state.status.errorRows > 0 && (
              <span className="text-destructive">
                Es gibt {state.status.errorRows} Zeile(n) mit Fehlern (z.B. fehlende Pflichtfelder). Bitte korrigiere sie in der Tabelle.
              </span>
            )}

            {/* Spezieller Hinweis für fehlende Mannschafts-Zuordnungen */}
            {!hasAllRequiredRelations && (
              <span className="text-destructive">
                Bitte wähle für alle Zeilen eine Mannschaft, bevor du den Import startest.
              </span>
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto shrink-0 sm:justify-end">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isImporting} className="h-11 px-6">
              Abbrechen
            </Button>
            <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <Button
                    type="button"
                    onClick={handleImportClick}
                    disabled={!canImport}
                    className="h-11 px-8 min-w-[140px]"
                >
                    {isImporting ? (
                        <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importiere...
                        </>
                    ) : (
                        "Import starten"
                    )}
                </Button>
            </PermissionFeatureWrapper>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
